<script lang="ts" setup>
import BaseButton from "@/components/base/BaseButton.vue";
import BaseModal from "@/components/base/BaseModal.vue";
import BasePromocode from "@/components/base/BasePromocode.vue";
import { useTariffPage } from "@/composables/tariff/useTariffPage";
import type { TariffArray } from "@/struct/TariffStruct";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";
import BaseIcon from "@/components/base/BaseIcon.vue";

const {
  changeTariffLaterLoading,
  changeTariffNowLoading,
  chosenTariff,
  closeModals,
  discountPercent,
  discountName,
  hasDiscount,
  doChange,
  formatAmountInput,
  getUnits,
  isChangeTariff,
  isDetailsTariff,
  isSticky,
  mainTariff,
  modalsList,
  offerPricePeriodLabel,
  pageTitle,
  pricePeriodLabel,
  returnServiceIcons,
  startChange,
  tariffDetails,
  tariffFullPrice,
  tariffRenewalPrice,
  tariffServices,
  availableTariffs,
  t,
  toPayment,
  updateOffers,
} = useTariffPage();
</script>

<template>
  <div class="tariff">
    <div class="tariff-header">
      <router-link to="account">
        <div class="tariff-header__back">
          <BaseFigureForIcon
            size="medium"
            color="white"
            icon="arrow"
            position="left"
            is-button
          />
        </div>
      </router-link>
      <div class="tariff-header__title" :class="isSticky ? 'visible' : ''">
        <p class="h3">
          {{ pageTitle }}
        </p>
      </div>
      <BaseFigureForIcon
        v-if="isChangeTariff || isDetailsTariff"
        class="tariff-header__close"
        icon="close"
        color="white"
        size="medium"
        is-button
        @click="
          isChangeTariff = false;
          isDetailsTariff = false;
        "
      />
    </div>
    <div v-if="isChangeTariff || isDetailsTariff" class="tariff-mobile_header">
      <p class="h1">
        {{ pageTitle }}
      </p>
    </div>
    <!--
      Три режима страницы переключаются кроссфейдом. У каждой ветки должен быть
      ровно один корневой элемент, поэтому «смена тарифа» обёрнута в
      .tariff-section — это обычный блок, раскладка не меняется.
    -->
    <Transition name="tab" mode="out-in">
      <div
        v-if="!isChangeTariff && !isDetailsTariff"
        key="main"
        class="tariff-body"
      >
        <div class="tariff-body__header">
          <div class="tariff-body__header-title">
            <p class="h1">
              {{ mainTariff.title }}
            </p>
          </div>
          <div class="tariff-body__header-amount">
            <p class="caption-medium">
              <template v-if="hasDiscount">
                {{ formatAmountInput(tariffRenewalPrice) }} ₽
                <span class="tariff-body__header-amount__old">
                  {{ formatAmountInput(tariffFullPrice) }} ₽
                </span>
              </template>
              <template v-else>
                {{ formatAmountInput(tariffFullPrice) }} ₽
              </template>
              {{ pricePeriodLabel }}
            </p>
          </div>
          <div v-if="discountPercent" class="tariff-body__header-discount">
            <p class="caption-medium color-grey-light">
              {{ discountName }}
            </p>
          </div>
        </div>
        <div class="tariff-body__buttons">
          <a href="/docs/offerte_msc.pdf" target="_blank">
            <button class="tariff-body__buttons-item">
              <p class="button-small">{{ t("common.labels.offer") }}</p>
              <div class="tariff-body__buttons-item__icon">
                <BaseIcon name="download" />
              </div>
            </button>
          </a>
          <button
            class="tariff-body__buttons-item"
            @click="isChangeTariff = true"
          >
            <p class="button-small">{{ t("tariff.page.change") }}</p>
            <div class="tariff-body__buttons-item__icon">
              <BaseIcon name="arrow" />
            </div>
          </button>
        </div>
        <div class="tariff-body__amounts">
          <div
            v-for="(item, index) in mainTariff.options"
            :key="index"
            class="tariff-body__amounts-item"
          >
            <p class="h2 color-white">
              {{ item.maximum }} {{ getUnits(item.id) }}
            </p>
          </div>
        </div>
        <div v-if="tariffServices.length" class="tariff-body__unlimited">
          <div class="tariff-body__unlimited-title">
            <p class="caption-medium--bold">{{ t("tariff.page.unlimited") }}</p>
          </div>
          <div class="tariff-body__icons">
            <div
              v-for="(img, index) in returnServiceIcons(tariffServices)"
              :key="index"
              class="tariff-body__icons-item"
            >
              <BaseIcon :name="img" folder="service" is-original-color />
            </div>
          </div>
        </div>
      </div>
      <div
        v-else-if="isChangeTariff && !isDetailsTariff"
        key="change"
        class="tariff-section"
      >
        <div class="tariff-promo">
          <BasePromocode @action="updateOffers" />
        </div>
        <div class="tariff-change">
          <div
            v-for="(item, index) in availableTariffs"
            :key="index"
            class="tariff-change__item"
          >
            <div
              v-if="item.operator === 'green'"
              class="tariff-change__item-icon"
            >
              <img
                src="@/assets/img/icons/moi-operator-tariff.svg"
                alt="operator-icon"
              />
            </div>
            <div class="tariff-change__item-title">
              <p class="caption-large--bold">{{ item.name }}</p>
            </div>
            <div class="tariff-change__item-subtitle">
              <p class="caption-medium color-text-secondary">
                {{
                  `${item.renewal_price}₽ ${offerPricePeriodLabel(
                    item.duration
                  )}`
                }}
              </p>
            </div>
            <div class="tariff-change__item-counters">
              <div class="tariff-change__item-counter">
                <p class="caption-medium--bold color-white">
                  {{ item.data }} {{ getUnits("gb") }}
                </p>
              </div>
              <div class="tariff-change__item-counter">
                <p class="caption-medium--bold color-white">
                  {{ item.min }} {{ getUnits("min") }}
                </p>
              </div>
              <div class="tariff-change__item-counter">
                <p class="caption-medium--bold color-white">
                  {{ item.sms }} {{ getUnits("sms") }}
                </p>
              </div>
            </div>
            <div
              class="tariff-change__item-unlimited"
              v-if="item.service.length"
            >
              <div class="tariff-change__item-unlimited--title">
                <p class="caption-medium color-text-primary">
                  {{ t("tariff.page.unlimited") }}
                </p>
              </div>
              <div class="tariff-change__item-unlimited--icons">
                <div
                  v-for="(img, index) in returnServiceIcons(item.service)"
                  :key="index"
                  class="tariff-change__item-unlimited--icon"
                >
                  <BaseIcon :name="img" folder="service" is-original-color />
                </div>
              </div>
            </div>
            <div class="tariff-change__item-btns">
              <div class="tariff-change__item-btn">
                <BaseButton
                  :title="t('common.actions.details')"
                  color="grey"
                  size="small"
                  @click="tariffDetails(item)"
                />
              </div>
              <div class="tariff-change__item-btn">
                <BaseButton
                  :title="t('common.actions.connect')"
                  size="small"
                  @click="startChange(item)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else key="details" class="tariff-details">
        <div class="tariff-details__header">
          <div class="tariff-details__title">
            <p class="h1">
              {{ chosenTariff?.name }}
            </p>
          </div>
          <div class="tariff-details__subtitle">
            <p class="caption-medium">
              {{
                `${chosenTariff?.price}₽ ${offerPricePeriodLabel(
                  chosenTariff?.duration
                )}`
              }}
            </p>
          </div>
          <!--          <div class="tariff-details__note">-->
          <!--            <p class="caption-medium color-text-secondary">-->
          <!--              скидка 50%, распостраняется на следующее списание-->
          <!--            </p>-->
          <!--          </div>-->
        </div>
        <div class="tariff-details__amounts">
          <div class="tariff-details__amounts-item">
            <p class="h2 color-white">
              {{ chosenTariff.data }} {{ getUnits("gb") }}
            </p>
          </div>
          <div class="tariff-details__amounts-item">
            <p class="h2 color-white">
              {{ chosenTariff.min }} {{ getUnits("min") }}
            </p>
          </div>
          <div class="tariff-details__amounts-item">
            <p class="h2 color-white">
              {{ chosenTariff.sms }} {{ getUnits("sms") }}
            </p>
          </div>
        </div>
        <div class="tariff-details__unlimited">
          <div class="tariff-details__unlimited--title">
            <p class="caption-medium color-text-primary">
              {{ t("tariff.page.unlimited") }}
            </p>
          </div>
          <div class="tariff-details__unlimited--icons">
            <div
              v-for="(img, index) in returnServiceIcons(chosenTariff.service)"
              :key="index"
              class="tariff-details__unlimited--icon"
            >
              <BaseIcon :name="img" folder="service" is-original-color />
            </div>
          </div>
        </div>
        <div class="tariff-details__actions">
          <a href="/docs/offerte_msc.pdf" target="_blank">
            <div class="tariff-details__actions-btn">
              <p class="button-small color-text-primary">
                {{ t("common.labels.offer") }}
              </p>
              <div class="tariff-details__actions-btn__icon">
                <BaseIcon name="download" />
              </div>
            </div>
          </a>
        </div>
        <div class="tariff-details__btn">
          <BaseButton
            :title="t('common.actions.connect')"
            @click="startChange(chosenTariff as TariffArray)"
          />
        </div>
      </div>
    </Transition>
    <Teleport to=".root">
      <BaseModal
        v-if="modalsList.changeModal"
        :title="
          t('tariff.details.connectTariffTitle', { name: chosenTariff?.name })
        "
        :text="t('tariff.details.connectTariffText')"
        :main-btn-title="t('tariff.details.connectNow')"
        :secondary-btn-title="t('tariff.details.connectLater')"
        @close="closeModals"
        @action="doChange('now')"
        @secondary-action="doChange('later')"
        :main-btn-loading="changeTariffNowLoading"
        :secondary-btn-loading="changeTariffLaterLoading"
      />
      <BaseModal
        v-if="modalsList.poorModal"
        :title="t('tariff.modals.insufficientFundsTitle')"
        :main-btn-title="t('tariff.modals.insufficientFundsAction')"
        @close="closeModals"
        @action="toPayment"
      />
      <BaseModal
        v-if="modalsList.commonError"
        :title="t('tariff.modals.connectionErrorTitle')"
        :main-btn-title="t('tariff.modals.tryAgain')"
        @close="closeModals"
        @action="closeModals"
      />
      <BaseModal
        v-if="modalsList.sameTariff"
        :title="t('tariff.modals.sameTariffTitle')"
        :main-btn-title="t('tariff.modals.tryAgain')"
        @close="closeModals"
        @action="closeModals"
      />
      <BaseModal
        v-if="modalsList.regionError"
        :title="t('tariff.modals.regionErrorTitle')"
        :main-btn-title="t('tariff.modals.tryAgain')"
        @close="closeModals"
        @action="closeModals"
      />
      <BaseModal
        v-if="modalsList.notFound"
        :title="t('tariff.modals.notFoundTitle')"
        :main-btn-title="t('tariff.modals.tryAgain')"
        @close="closeModals"
        @action="closeModals"
      />
      <BaseModal
        v-if="modalsList.noPlan"
        :title="t('tariff.modals.noPlanTitle')"
        :main-btn-title="t('tariff.modals.tryAgain')"
        @close="closeModals"
        @action="closeModals"
      />
      <BaseModal
        v-if="modalsList.successModal"
        :title="t('tariff.modals.successTitle')"
        :main-btn-title="t('common.actions.ok')"
        @close="closeModals"
        @action="closeModals"
      />
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/components/tariff";
</style>
